// Pure helpers for the Actions module. No Firestore access here — see
// lib/db.ts for writes and hooks/useScopedData.ts for reads.
//
// ---- Architecture note: future system-generated Actions ------------------
// Phase 1 only ever writes source: 'manual'. A later automated generator
// (SYT/advancement/recharter/leadership — see ActionSourceType) will need
// to re-evaluate a condition on a schedule (e.g. "any SYT within 30 days of
// expiring") without creating a duplicate Action every time it runs, even
// under concurrency (a retried import, two overlapping scheduled runs).
//
// REVISED (Phase 1 hardening pass): a plain "query for an existing Action
// by sourceKey, create one if none is found" is NOT safe — that's two
// separate round trips, and two concurrent callers can both run the query,
// both see nothing yet, and both create an Action for the same condition.
// The real mechanism is `findOrCreateActionByKey()` in lib/db.ts: a
// Firestore transaction over a small shared `actionLocks/{sourceKey}` doc
// (see ActionLock in types.ts). Firestore's transaction isolation
// guarantees only one of two conflicting transactions on that same lock
// document can commit; the loser is automatically retried by the SDK and,
// on retry, observes the lock the winner just created — so it returns the
// existing actionId instead of creating a second Action. This has been
// proven under an actual 20-way concurrent race, not just reasoned about —
// see scripts/test-actions-idempotency.ts (npm run test:idempotency).
//
// The schema pieces this relies on:
//   - `sourceKey` (Action, in types.ts): a deterministic string the
//     generator derives from the real-world condition, e.g.
//     `syt:<memberId>:<unitId>`.
//   - `actionLocks/{sourceKey}` (ActionLock, in types.ts): the uniqueness
//     marker itself, pointing at the Action doc id it protects. Deleted via
//     releaseActionLock() when that Action resolves (completed/cancelled),
//     freeing the key for a fresh Action if the same condition recurs
//     later (e.g. the person's SYT expires again next year) — so
//     uniqueness is scoped to "currently active," not forever.
//   - `source` / `sourceType`: which generator produced it and why, so the
//     UI can label it distinctly from manual work and a generator can
//     filter to just its own Actions when re-evaluating.
//   - `relatedUnitId` / `relatedContactId`: what the condition is about,
//     so the generated title/description and any future contextual
//     display (e.g. "Actions for this unit" on the Unit Health drawer)
//     has something concrete to key off.
//
// None of this is wired to real automation in Phase 1 — nothing calls
// findOrCreateActionByKey() yet — but the mechanism itself is implemented
// and tested, so Phase 2 can build directly on it rather than inventing
// (and re-proving) its own concurrency strategy.

import { actionIsResolved, type Action, type ActionSourceType, type ActionStatus } from '../types.ts';

/** Raised after a successful local Action mutation so the callable-backed
 * allowlist refreshes without restoring direct Firestore reads. */
export const ACTIONS_CHANGED_EVENT = 'compass:actions-changed';

/** Same "resolved" definition as the one on the type module, re-exported
 *  here so callers that only need the pure Action helpers don't have to
 *  import from types.ts too. */
export { actionIsResolved };

// ---- Action "type" (source/sourceType) filter -----------------------------
// The Actions page's Type filter, and the "Created by Compass · X" drawer
// badge, both need one canonical mapping from an Action's source/sourceType
// to a user-facing label — built from the real ActionSourceType model
// (types.ts), not by parsing titles or hand-rolling per-automation logic.
// A manual Action has no sourceType at all, so 'manual' is a sentinel
// bucket alongside the real ActionSourceType values (safe: no real
// sourceType is ever the literal string 'manual').

export type ActionTypeFilterValue = 'manual' | ActionSourceType;

/** Every value ActionTypeFilterValue can take, typed so TypeScript forces
 *  a label here the moment a new ActionSourceType is added to types.ts —
 *  the single source of truth both the filter dropdown and the drawer
 *  badge read from. */
export const ACTION_TYPE_LABELS: Record<ActionTypeFilterValue, string> = {
  manual: 'Manual',
  syt: 'SYT Follow-up',
  advancementStalled: 'Advancement Follow-up',
  recharter: 'Unit Renewal Follow-up',
  goal: 'Goal Follow-up',
  initiative: 'Initiative Follow-up',
  leadership: 'Leadership Follow-up',
  hazardousWeather: 'Hazardous Weather Follow-up',
};

/** The options actually offered in the Actions page's Type filter — the
 *  subset of ACTION_TYPE_LABELS whose automation is real today (writes
 *  actual Actions). 'goal'/'initiative'/'leadership' stay reserved in
 *  ActionSourceType/ACTION_TYPE_LABELS for a future generator but aren't
 *  offered yet — nothing sets them, so showing them would only ever
 *  filter to an empty table. Extend this array — nothing else in the
 *  Actions page — when the next automation ships. */
export const ACTION_TYPE_FILTER_ORDER: ActionTypeFilterValue[] = ['manual', 'syt', 'advancementStalled', 'recharter', 'hazardousWeather'];

/** The Type-filter bucket a given Action belongs to: 'manual' for
 *  anything hand-created, otherwise its sourceType. Returns a plain
 *  string (not the narrower ActionTypeFilterValue) so a system Action
 *  that's somehow missing sourceType — shouldn't happen per the schema,
 *  but data can always surprise you — degrades to a labeled "Other"
 *  bucket instead of throwing. */
export function actionTypeKey(a: Pick<Action, 'source' | 'sourceType'>): string {
  return a.source === 'manual' ? 'manual' : (a.sourceType ?? 'unknown');
}

/** Falls back to the raw key itself for anything not yet in
 *  ACTION_TYPE_LABELS (mirrors the drawer badge's original fallback
 *  behavior) rather than showing nothing. */
export function actionTypeLabel(key: string): string {
  return (ACTION_TYPE_LABELS as Record<string, string>)[key] ?? (key === 'unknown' ? 'Other' : key);
}

export function actionMatchesTypeFilter(a: Pick<Action, 'source' | 'sourceType'>, typeFilter: 'all' | string): boolean {
  return typeFilter === 'all' || actionTypeKey(a) === typeFilter;
}

export function actionIsOverdue(a: Pick<Action, 'dueDate' | 'status'>): boolean {
  if (!a.dueDate || a.status === 'completed' || a.status === 'cancelled') return false;
  return a.dueDate < new Date().toISOString().slice(0, 10);
}

export function actionIsDueSoon(a: Pick<Action, 'dueDate' | 'status'>, withinDays = 7): boolean {
  if (!a.dueDate || a.status === 'completed' || a.status === 'cancelled') return false;
  if (actionIsOverdue(a)) return false;
  const now = new Date();
  const soon = new Date();
  soon.setDate(now.getDate() + withinDays);
  const d = new Date(a.dueDate + 'T00:00:00');
  return d >= now && d <= soon;
}

/** 'YYYY-MM-DD' -> "Sep 16" — never show the raw ISO form to a user. */
export function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Human due-date display for the Actions list/drawer: "Sep 16" plus a
 *  short operational context ("Overdue 2 days" / "Due today" / "Due in 3
 *  days") — only within a week either side, so a distant due date doesn't
 *  get cluttered with a running day-count, and a resolved Action never
 *  shows one at all (its due date is no longer operationally relevant). */
export function actionDueDateDisplay(
  a: Pick<Action, 'dueDate' | 'status'>,
): { text: string; context: string | null } {
  if (!a.dueDate) return { text: '—', context: null };
  const text = fmtDate(a.dueDate);
  if (actionIsResolved(a.status)) return { text, context: null };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(a.dueDate + 'T00:00:00');
  const days = Math.round((due.getTime() - today.getTime()) / 86_400_000);

  if (days < 0) return { text, context: `Overdue ${-days} day${-days === 1 ? '' : 's'}` };
  if (days === 0) return { text, context: 'Due today' };
  if (days <= 7) return { text, context: `Due in ${days} day${days === 1 ? '' : 's'}` };
  return { text, context: null };
}

/** Sort key for the default Actions list: overdue first, then by due date
 *  (soonest first, undated last), then by priority (critical first). */
const PRIORITY_RANK: Record<Action['priority'], number> = {
  critical: 0,
  high: 1,
  normal: 2,
  low: 3,
};

export function compareActions(a: Action, b: Action): number {
  const aOverdue = actionIsOverdue(a);
  const bOverdue = actionIsOverdue(b);
  if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;
  const aDue = a.dueDate ?? '9999-99-99';
  const bDue = b.dueDate ?? '9999-99-99';
  if (aDue !== bDue) return aDue.localeCompare(bDue);
  return PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
}

export function statusChangedText(from: ActionStatus, to: ActionStatus, labels: Record<ActionStatus, string>): string {
  return `changed status from ${labels[from]} to ${labels[to]}.`;
}
